import { css } from "@emotion/css";
import React from "react";
import { VisuallyHidden } from "react-aria";
import { ColumnProps, TableBodyProps } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { useColors } from "../../hooks/useColors";

import { DataField } from "./DataField";
import { DataFieldTable } from "./DataFieldTable";
import { DataCell } from "./assets/DataCell";
import { DataColumn } from "./assets/DataColumn";
import { DataTableHeader } from "./assets/DataTableHeader";
import { Row } from "./assets/Row";
import { Table, TableProps } from "./assets/Table";
import { TableBody } from "./assets/TableBody";
import { Text } from "../../foundations/typography/Text";
import { MediaQuery } from "../../utils/MediaQuery";
import { textAlignment } from "../data-tables/assets/Cell";
import { Loader } from "../loaders/loading/Loader";
import { NeutralBadge } from "../badge";

interface ColumnDefinition<T> {
  children?: ColumnDefinition<T>[];
  id?: ColumnProps["id"];
  isRowHeader?: ColumnProps["isRowHeader"];
  key: string;
  maxWidth?: ColumnProps["maxWidth"];
  minWidth?: ColumnProps["minWidth"];
  name?: string;
  render?(item: T, key?: string): React.ReactNode;
  textValue?: string;
  width?: ColumnProps["width"];
  textAlignment?: textAlignment;
  columnContentBold?: boolean;
  headerBadge?: string;
}

interface Props<T> extends TableProps {
  columns: ColumnDefinition<T>[];
  rows: T[];
  testId?: string;
  isLoading?: boolean;
  renderEmptyState?: TableBodyProps<T>["renderEmptyState"];
  renderTopMargin?: boolean;
}

export function DataDisplay<T extends object & { isDisabled?: boolean }>(
  props: Props<T>
) {
  const { t } = useTranslation();
  const colors = useColors();
  const {
    columns,
    rows,
    renderEmptyState,
    testId = "data-table",
    renderTopMargin = true,
  } = props;

  const defaultRender = (item: T, key: string) => {
    const itemValue = item[key as keyof T];
    return React.isValidElement(itemValue) || typeof itemValue === "string"
      ? itemValue
      : null;
  };

  const findColumnByKey = (key: string) => {
    return columns.find((column) => column.key === key);
  };

  return (
    <div
      data-testid={testId}
      className={css(
        styles.table,
        renderTopMargin && { marginBlockStart: "var(--space-6)" }
      )}
    >
      <MediaQuery sm md>
        {rows.map((row, index) => (
          <DataFieldTable key={index}>
            {columns.map(({ name, key, columnContentBold, headerBadge }) => (
              <DataField
                key={key}
                name={
                  headerBadge ? (
                    <span className={css(styles.columnHeader)}>
                      {name}
                      <NeutralBadge
                        type="subtle"
                        size="large"
                        testID={`${key}-header-badge`}
                      >
                        {headerBadge}
                      </NeutralBadge>
                    </span>
                  ) : (
                    name
                  )
                }
                value={row[key as keyof T] as string}
                className={
                  columnContentBold ? styles.columnContentBold : undefined
                }
              />
            ))}
          </DataFieldTable>
        ))}
      </MediaQuery>
      <MediaQuery lg xl>
        <Table data-testid={`${testId}-lg`} {...props}>
          <DataTableHeader columns={columns}>
            {(column) => (
              <DataColumn
                textAlignment={column.textAlignment}
                id={column.id || column.key}
                isRowHeader={column.isRowHeader}
                key={column.key}
                maxWidth={column.minWidth}
                minWidth={column.maxWidth}
                width={column.width}
                textValue={column.textValue || column.name}
              >
                {column.name ? (
                  <span className={css(styles.columnHeader)}>
                    {column.name}
                    {column.headerBadge && (
                      <NeutralBadge
                        type="subtle"
                        size="large"
                        testID={`${column.key}-header-badge`}
                      >
                        {column.headerBadge}
                      </NeutralBadge>
                    )}
                  </span>
                ) : (
                  <VisuallyHidden>{column.textValue}</VisuallyHidden>
                )}
              </DataColumn>
            )}
          </DataTableHeader>
          <TableBody
            items={rows}
            renderEmptyState={(renderProps) => {
              if (props.isLoading) {
                return (
                  <div className={styles.empty}>
                    <Loader />
                  </div>
                );
              }

              return (
                <div className={styles.empty}>
                  {renderEmptyState ? (
                    renderEmptyState(renderProps)
                  ) : (
                    <Text
                      style="global/body-secondary"
                      align="center"
                      color="text-weak"
                    >
                      {t("No results found.")}
                    </Text>
                  )}
                </div>
              );
            }}
          >
            {(item) => (
              <Row columns={columns}>
                {({ key, render }) => (
                  <DataCell
                    textAlignment={findColumnByKey(key)?.textAlignment}
                    key={key}
                    className={
                      item.isDisabled
                        ? css(
                            findColumnByKey(key)?.columnContentBold
                              ? styles.columnContentBold
                              : undefined,
                            {
                              color: colors["text-disabled"],
                              background:
                                colors["background-accent-neutral-weak"],
                            }
                          )
                        : css(
                            findColumnByKey(key)?.columnContentBold
                              ? styles.columnContentBold
                              : undefined
                          )
                    }
                  >
                    {render ? render(item) : defaultRender(item, key)}
                  </DataCell>
                )}
              </Row>
            )}
          </TableBody>
        </Table>
      </MediaQuery>
      {rows.length > 0 && props.isLoading && (
        <Loader overlay background="background-inverse-ghost-hover" />
      )}
    </div>
  );
}

const styles = {
  empty: css({
    padding: "var(--space-6)",
    minHeight: 288,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  table: css({
    display: "relative",
  }),
  columnContentBold: css({
    fontWeight: "var(--font-weight-medium)",

    "& *": {
      fontWeight: "var(--font-weight-medium)",
    },
  }),
  columnHeader: css({
    display: "inline-flex",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
};
